import classNames from 'classnames';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import IconButton from '@alltrails/denali/components/IconButton';
import { usePopover } from '@alltrails/denali/components/Popover';
import Close from '@alltrails/denali/icons/Close';
import { COLOR_BACKGROUND_INVERSE, COLOR_BACKGROUND_PRIMARY, SPACE_150 } from '@alltrails/denali/tokens';
import type { ColorsTheme } from '@alltrails/types/ColorsTheme';
import styles from './DismissableCallout.module.scss';

type DismissableCalloutProps = Pick<Parameters<typeof usePopover>[0], 'anchor' | 'placement'> & {
  className?: string;
  title: ReactNode;
  body: ReactNode;
  isOpen: boolean;
  onDismiss: () => void;
  theme?: ColorsTheme;
};

const DismissableCallout = ({ className, title, body, isOpen, onDismiss, theme = 'dark', ...usePopoverProps }: DismissableCalloutProps) => {
  const intl = useIntl();
  const isLight = theme === 'light';
  const { anchorElement, arrowElement, isPositioned, popoverRef, popoverStyle, popoverInteractiveProps } = usePopover({
    ...usePopoverProps,
    arrowColor: isLight ? COLOR_BACKGROUND_PRIMARY : COLOR_BACKGROUND_INVERSE,
    mainAxisOffset: SPACE_150
  });

  return (
    <div>
      {anchorElement}
      {isOpen && (
        <div
          ref={popoverRef}
          {...popoverInteractiveProps}
          // Hidden rather than unmounted: floating-ui needs the element in the DOM to measure it
          style={{ ...popoverStyle, visibility: isPositioned ? undefined : 'hidden' }}
          className={classNames(styles.container, { [styles.light]: isLight }, className)}
        >
          {arrowElement}
          <Typography className={styles.title} component="div" variant="heading200" color={isLight ? 'primary' : 'inverse'}>
            {title}
          </Typography>
          <Typography component="div" variant="text100" color={isLight ? 'secondary' : 'inverse'}>
            {body}
          </Typography>
          <IconButton
            className={styles.closeButton}
            icon={{ Component: Close }}
            onClick={onDismiss}
            size="sm"
            theme={isLight ? 'light' : 'dark'}
            title={intl.formatMessage({ defaultMessage: 'Close' })}
            variant="ghost"
          />
        </div>
      )}
    </div>
  );
};

export default DismissableCallout;
